import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
} from "antd";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import {
  adminManualSubscription,
  getAdminManualSubscriptionsList,
} from "../../utils/organization";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  History,
  ShieldCheck,
  Unlock,
  XCircle,
} from "lucide-react";

const { Option } = Select;
const { TextArea } = Input;

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer / Wire" },
  { value: "check", label: "Cheque" },
  { value: "invoice", label: "Direct Invoicing (External)" },
  { value: "cash", label: "Cash" },
  { value: "manual_card", label: "Manual Card (Processed Externally)" },
  { value: "complimentary", label: "Complimentary / Promo" },
  { value: "other", label: "Other" },
];

const AdminManualSubscriptionModal = ({
  modalStatus,
  setModalStatus,
  organization,
  organizations: propOrganizations,
  onSuccess,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  // Normalize single vs multiple organizations
  const orgList = (
    Array.isArray(propOrganizations) && propOrganizations.length > 0
      ? propOrganizations
      : organization
      ? [organization]
      : []
  ).filter(Boolean);

  const isBatch = orgList.length > 1;
  const singleOrg = orgList[0] || null;

  const [activeTab, setActiveTab] = useState("action");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);

  // Form State (Shared / Defaults)
  const [action, setAction] = useState("activate");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [amountPaid, setAmountPaid] = useState(null);
  const [renewalDate, setRenewalDate] = useState(dayjs().add(1, "month"));
  const [paymentNote, setPaymentNote] = useState("");
  const [notifyUser, setNotifyUser] = useState(true);

  // Plan modules (Shared defaults)
  const [modules, setModules] = useState({
    accounts: true,
    files: false,
    history_and_notes: false,
    payroll: false,
  });

  // Per-organization plan configs when batch mode is active
  const [perOrgPlans, setPerOrgPlans] = useState({});

  const orgId = singleOrg?.id;
  const orgName = isBatch
    ? `${orgList.length} Selected Organizations`
    : singleOrg?.account || singleOrg?.name || "Organization";
  const provider = (singleOrg?.provider || "xero").toLowerCase();
  const hasXero = orgList.some((o) => (o.provider || "xero").toLowerCase() === "xero");
  const hasQbo = orgList.some((o) => (o.provider || "").toLowerCase() === "qbo");
  const isQbo = !isBatch && provider === "qbo";
  const isSubscribed = singleOrg?.org_status == 1 || singleOrg?.subscription_status == 1;
  const isVault = singleOrg?.subscription_type === "inactive_orgs_sub";
  const isAdminBilling = singleOrg?.admin_billing_mode == 1;

  useEffect(() => {
    if (modalStatus && orgList.length > 0) {
      setActiveTab("action");
      if (!isBatch && isSubscribed) {
        setAction("extend");
      } else if (!isBatch && isVault) {
        setAction("activate");
      } else {
        setAction("activate");
      }

      const existingRenewal = singleOrg?.subscription_renewal_date;
      if (existingRenewal && dayjs(existingRenewal).isValid()) {
        const d = dayjs(existingRenewal);
        setRenewalDate(d.isAfter(dayjs()) ? d.add(1, "month") : dayjs().add(1, "month"));
      } else {
        setRenewalDate(dayjs().add(1, "month"));
      }

      setPaymentMethod("bank_transfer");
      setAmountPaid(null);
      setPaymentNote("");
      setNotifyUser(true);

      const initialSharedModules = {
        accounts: true,
        files: !!singleOrg?.files,
        history_and_notes: !!singleOrg?.history_and_notes,
        payroll: !!singleOrg?.payroll,
      };
      setModules(initialSharedModules);

      // Build initial per-organization map
      const initialMap = {};
      orgList.forEach((o) => {
        const oKey = o.id || o.statusKey;
        const oProv = (o.provider || "xero").toLowerCase();
        initialMap[oKey] = {
          id: o.id,
          provider: oProv,
          name: o.account || o.name || `Org #${o.id}`,
          country_code: o.country_code || o.country || "",
          files: !!o.files,
          history_and_notes: !!o.history_and_notes,
          payroll: !!o.payroll,
          amount_paid: null,
          custom_renewal_date: null,
        };
      });
      setPerOrgPlans(initialMap);

      if (!isBatch) {
        fetchHistory();
      } else {
        setHistoryData([]);
      }
    }
  }, [modalStatus, organization, propOrganizations]);

  const applySharedModulesToAll = (updatedSharedModules) => {
    setPerOrgPlans((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (next[k].provider !== "qbo") {
          next[k] = {
            ...next[k],
            files: updatedSharedModules.files,
            history_and_notes: updatedSharedModules.history_and_notes,
            payroll: updatedSharedModules.payroll,
          };
        }
      });
      return next;
    });
  };

  const handlePerOrgToggle = (orgKey, field, value) => {
    setPerOrgPlans((prev) => ({
      ...prev,
      [orgKey]: {
        ...prev[orgKey],
        [field]: value,
      },
    }));
  };

  const fetchHistory = async () => {
    if (!orgId || isBatch) return;
    setHistoryLoading(true);
    try {
      const token = await getIdTokenClaims();
      const res = await getAdminManualSubscriptionsList(token.__raw, orgId, provider);
      if (res.data?.success) {
        setHistoryData(res.data.history || []);
      }
    } catch (err) {
      console.error("Error fetching manual subscription history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (orgList.length === 0) {
      enqueueSnackbar("No organizations selected.", { variant: "error" });
      return;
    }

    if (["activate", "extend", "downgrade"].includes(action) && !renewalDate) {
      enqueueSnackbar("Please select a renewal / expiry date.", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const token = await getIdTokenClaims();

      let payload;
      if (isBatch) {
        payload = {
          action,
          payment_method: paymentMethod,
          payment_note: paymentNote ? paymentNote.trim() : null,
          subscription_renewal_date: renewalDate ? renewalDate.format("YYYY-MM-DD") : null,
          notify_user: notifyUser,
          orgs: orgList.map((o) => {
            const oKey = o.id || o.statusKey;
            const p = perOrgPlans[oKey] || {};
            const oProv = (o.provider || "xero").toLowerCase();
            return {
              id: o.id,
              provider: oProv,
              amount_paid: p.amount_paid !== null && p.amount_paid !== undefined && p.amount_paid !== ""
                ? Number(p.amount_paid)
                : (amountPaid !== null ? Number(amountPaid) : null),
              subscription_renewal_date: p.custom_renewal_date
                ? dayjs(p.custom_renewal_date).format("YYYY-MM-DD")
                : (renewalDate ? renewalDate.format("YYYY-MM-DD") : null),
              plan: oProv !== "qbo"
                ? {
                    accounts: true,
                    files: p.files ?? modules.files,
                    history_and_notes: p.history_and_notes ?? modules.history_and_notes,
                    payroll: p.payroll ?? modules.payroll,
                  }
                : undefined,
            };
          }),
        };
      } else {
        payload = {
          org_id: orgId,
          provider,
          action,
          payment_method: paymentMethod,
          payment_note: paymentNote ? paymentNote.trim() : null,
          amount_paid: amountPaid !== null ? Number(amountPaid) : null,
          subscription_renewal_date: renewalDate ? renewalDate.format("YYYY-MM-DD") : null,
          notify_user: notifyUser,
          plan: !isQbo
            ? {
                accounts: true,
                files: modules.files,
                history_and_notes: modules.history_and_notes,
                payroll: modules.payroll,
              }
            : undefined,
        };
      }

      const res = await adminManualSubscription(token.__raw, payload);
      if (res.data?.success) {
        enqueueSnackbar(
          res.data.message || `Manual subscription ${action} completed successfully.`,
          { variant: "success" }
        );
        setModalStatus(false);
        onSuccess?.();
      } else {
        enqueueSnackbar(res.data?.message || "Failed to process manual subscription.", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error("Error updating manual subscription:", err);
      enqueueSnackbar(
        err?.response?.data?.message || "An error occurred while updating the subscription.",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (val) => (val ? dayjs(val).format("YYYY-MM-DD HH:mm") : "-"),
      width: "25%",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (val) => {
        const colors = {
          activate: "green",
          extend: "blue",
          set_renewal: "blue",
          downgrade: "orange",
          cancel: "red",
          release: "purple",
        };
        return <Tag color={colors[val] || "default"}>{val?.toUpperCase()}</Tag>;
      },
      width: "20%",
    },
    {
      title: "Method / Amount",
      key: "payment",
      render: (_, row) => (
        <div>
          <div className="font-medium text-xs text-gray-800">
            {PAYMENT_METHODS.find((p) => p.value === row.payment_method)?.label ||
              row.payment_method ||
              "-"}
          </div>
          {row.amount_paid ? (
            <div className="text-xs text-gray-500">${Number(row.amount_paid).toFixed(2)}</div>
          ) : null}
        </div>
      ),
      width: "30%",
    },
    {
      title: "Note",
      dataIndex: "payment_note",
      key: "payment_note",
      render: (val) => val || "-",
      width: "25%",
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            <div>
              <div className="text-xl font-semibold text-gray-800">
                {isBatch ? `Bulk Offline Billing (${orgList.length} Companies)` : "Manual / Offline Subscription"}
              </div>
              <div className="text-xs font-normal text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                {isBatch ? (
                  <>
                    <span className="font-medium text-gray-700">{orgList.length} organizations selected</span>
                    {hasXero && <Tag color="blue">Xero</Tag>}
                    {hasQbo && <Tag color="green">QuickBooks</Tag>}
                    <Tag color="purple">Batch Admin Mode</Tag>
                  </>
                ) : (
                  <>
                    <span>{orgName}</span>
                    <Tag color={isQbo ? "green" : "blue"}>{provider.toUpperCase()}</Tag>
                    {isAdminBilling ? (
                      <Tag color="purple">Admin Managed</Tag>
                    ) : (
                      <Tag color="cyan">Stripe Self-Service</Tag>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      }
      centered
      width={isBatch ? 780 : 680}
      open={modalStatus}
      onCancel={() => setModalStatus(false)}
      footer={
        activeTab === "action" ? (
          <div className="flex justify-between items-center px-2 py-1">
            <div className="text-xs text-gray-400">
              * Operates independently of Stripe card payments.
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => setModalStatus(false)}>Cancel</Button>
              <Button
                type="primary"
                loading={loading}
                onClick={handleSubmit}
                className="bg-blue-600 hover:bg-blue-700 font-medium"
              >
                {isBatch
                  ? `Apply ${action.charAt(0).toUpperCase() + action.slice(1)} to ${orgList.length} Companies`
                  : `Apply ${action.charAt(0).toUpperCase() + action.slice(1)}`}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end px-2 py-1">
            <Button onClick={() => setModalStatus(false)}>Close</Button>
          </div>
        )
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={(k) => {
          setActiveTab(k);
          if (k === "history") fetchHistory();
        }}
        items={[
          {
            key: "action",
            label: (
              <span className="flex items-center gap-1.5 font-medium">
                <CreditCard className="w-4 h-4" /> Manage Subscription
              </span>
            ),
            children: (
              <div className="py-2 space-y-4 text-sm max-h-[75vh] overflow-y-auto pr-1">
                {/* Action Selector */}
                <div>
                  <label className="block font-medium text-gray-700 mb-1.5">
                    Select Administrative Action
                  </label>
                  <Radio.Group
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    className="w-full grid grid-cols-2 sm:grid-cols-3 gap-2"
                  >
                    <Radio.Button
                      value="activate"
                      className="text-center flex items-center justify-center h-10 rounded-lg"
                    >
                      🚀 Activate
                    </Radio.Button>
                    <Radio.Button
                      value="extend"
                      className="text-center flex items-center justify-center h-10 rounded-lg"
                    >
                      ⏳ Extend / Renew
                    </Radio.Button>
                    <Radio.Button
                      value="downgrade"
                      className="text-center flex items-center justify-center h-10 rounded-lg"
                    >
                      📦 Downgrade to Vault
                    </Radio.Button>
                    <Radio.Button
                      value="cancel"
                      className="text-center flex items-center justify-center h-10 rounded-lg text-red-600"
                    >
                      🛑 Cancel
                    </Radio.Button>
                    <Radio.Button
                      value="release"
                      className="text-center flex items-center justify-center h-10 rounded-lg text-purple-600"
                    >
                      🔓 Release Lock
                    </Radio.Button>
                  </Radio.Group>
                </div>

                {/* Action Specific Info */}
                {action === "release" && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-purple-800 text-xs flex items-start gap-2">
                    <Unlock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <b>Release Admin Lock:</b> This clears the manual admin billing lock (
                      <code>admin_billing_mode = 0</code>), allowing organization owners to
                      manage and pay for subscriptions via standard Stripe self-service checkout.
                    </div>
                  </div>
                )}

                {action === "downgrade" && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <b>Downgrade to Vault:</b> Disconnects live backup sync, retaining read-only archive access to all past backups.
                    </div>
                  </div>
                )}

                {action === "cancel" && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs flex items-start gap-2">
                    <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <b>Cancel Subscription:</b> Deactivates subscription, disconnects live syncing, and marks organizations as cancelled.
                    </div>
                  </div>
                )}

                {/* Shared Plan Modules Selection (Only for activate/extend on Xero) */}
                {["activate", "extend"].includes(action) && hasXero && (
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block font-medium text-gray-700 text-xs">
                        {isBatch ? "Default Modules for Xero Companies" : "Included Modules (Xero)"}
                      </label>
                      {isBatch && (
                        <span className="text-[11px] text-blue-600 cursor-pointer hover:underline" onClick={() => applySharedModulesToAll(modules)}>
                          Apply to all companies below
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <Checkbox checked disabled>
                        Accounts & Core Data (Included)
                      </Checkbox>
                      <Checkbox
                        checked={modules.files}
                        onChange={(e) => {
                          const updated = { ...modules, files: e.target.checked };
                          setModules(updated);
                          if (isBatch) applySharedModulesToAll(updated);
                        }}
                      >
                        Files & Attachments
                      </Checkbox>
                      <Checkbox
                        checked={modules.history_and_notes}
                        onChange={(e) => {
                          const updated = { ...modules, history_and_notes: e.target.checked };
                          setModules(updated);
                          if (isBatch) applySharedModulesToAll(updated);
                        }}
                      >
                        History & Notes
                      </Checkbox>
                      <Checkbox
                        checked={modules.payroll}
                        onChange={(e) => {
                          const updated = { ...modules, payroll: e.target.checked };
                          setModules(updated);
                          if (isBatch) applySharedModulesToAll(updated);
                        }}
                      >
                        Payroll Records
                      </Checkbox>
                    </div>
                  </div>
                )}

                {/* Batch Per-Company Table Section */}
                {isBatch && (
                  <div className="border border-gray-200 rounded-lg p-3 bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-800">
                        Selected Companies & Per-Organization Settings ({orgList.length})
                      </span>
                      <span className="text-[11px] text-gray-500">
                        Shared values below apply automatically unless overridden here
                      </span>
                    </div>

                    <div className="max-h-56 overflow-y-auto border border-gray-200 rounded bg-white divide-y divide-gray-100 text-xs">
                      {orgList.map((org) => {
                        const oKey = org.id || org.statusKey;
                        const plan = perOrgPlans[oKey] || {};
                        const oProv = (org.provider || "xero").toLowerCase();
                        const isOrgQbo = oProv === "qbo";

                        return (
                          <div key={oKey} className="p-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 hover:bg-gray-50 transition-colors">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-gray-800 flex items-center gap-1.5 truncate">
                                <span>{org.account || org.name}</span>
                                <Tag className="text-[10px] m-0" color={isOrgQbo ? "green" : "blue"}>
                                  {oProv.toUpperCase()}
                                </Tag>
                              </div>
                              <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                                <span>Owner: {org.org_owner_email || org.email || "N/A"}</span>
                              </div>
                            </div>

                            {/* Per-org module toggles for Xero */}
                            {!isOrgQbo && ["activate", "extend"].includes(action) && (
                              <div className="flex items-center gap-3 text-[11px]">
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 focus:ring-0"
                                    checked={plan.files ?? modules.files}
                                    onChange={(e) => handlePerOrgToggle(oKey, "files", e.target.checked)}
                                  />
                                  <span>Files</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 focus:ring-0"
                                    checked={plan.history_and_notes ?? modules.history_and_notes}
                                    onChange={(e) => handlePerOrgToggle(oKey, "history_and_notes", e.target.checked)}
                                  />
                                  <span>History</span>
                                </label>
                                <label className="flex items-center gap-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-blue-600 focus:ring-0"
                                    checked={plan.payroll ?? modules.payroll}
                                    onChange={(e) => handlePerOrgToggle(oKey, "payroll", e.target.checked)}
                                  />
                                  <span>Payroll</span>
                                </label>
                              </div>
                            )}

                            {/* Optional custom amount per org */}
                            <div className="w-24 shrink-0">
                              <InputNumber
                                size="small"
                                placeholder={amountPaid ? `$${amountPaid}` : "Amount ($)"}
                                min={0}
                                precision={2}
                                className="w-full text-xs"
                                value={plan.amount_paid}
                                onChange={(val) => handlePerOrgToggle(oKey, "amount_paid", val)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Date & Payment Details for Non-Release Actions */}
                {action !== "release" && (
                  <div className="space-y-3 border-t border-gray-200 pt-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {["activate", "extend", "downgrade"].includes(action) && (
                        <div>
                          <label className="block text-gray-700 text-xs font-medium mb-1">
                            Renewal / Expiry Date *
                          </label>
                          <DatePicker
                            className="w-full"
                            value={renewalDate}
                            onChange={(date) => setRenewalDate(date)}
                            format="YYYY-MM-DD"
                            allowClear={false}
                          />
                        </div>
                      )}

                      <div>
                        <label className="block text-gray-700 text-xs font-medium mb-1">
                          Offline Payment Method
                        </label>
                        <Select
                          className="w-full"
                          value={paymentMethod}
                          onChange={(v) => setPaymentMethod(v)}
                        >
                          {PAYMENT_METHODS.map((m) => (
                            <Option key={m.value} value={m.value}>
                              {m.label}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 text-xs font-medium mb-1">
                          Amount Paid ($) (Optional)
                        </label>
                        <InputNumber
                          className="w-full"
                          placeholder="e.g. 29.00"
                          min={0}
                          step={0.01}
                          precision={2}
                          value={amountPaid}
                          onChange={(val) => setAmountPaid(val)}
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-xs font-medium mb-1">
                          Payment Reference / Invoice #
                        </label>
                        <Input
                          placeholder="e.g. INV-2026-001 / Wire ref"
                          value={paymentNote}
                          onChange={(e) => setPaymentNote(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <span className="text-xs text-gray-600">
                        Send confirmation email to organization owner
                      </span>
                      <Switch
                        checked={notifyUser}
                        onChange={(checked) => setNotifyUser(checked)}
                        size="small"
                      />
                    </div>
                  </div>
                )}
              </div>
            ),
          },
          {
            key: "history",
            label: (
              <span className="flex items-center gap-1.5 font-medium">
                <History className="w-4 h-4" /> Audit History
              </span>
            ),
            children: (
              <div className="py-2">
                {historyLoading ? (
                  <div className="py-12 flex justify-center">
                    <Spin />
                  </div>
                ) : historyData.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    No manual subscription history found for this organization.
                  </div>
                ) : (
                  <Table
                    columns={historyColumns}
                    dataSource={historyData}
                    rowKey="id"
                    pagination={{ pageSize: 5 }}
                    size="small"
                  />
                )}
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
};

const providerLabel = (prov) => {
  if (prov === "qbo") return "QuickBooks Online";
  return "Xero";
};

export default AdminManualSubscriptionModal;
